import { Component, Inject, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';

import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AuthService } from 'src/app/services/auth.service';
import { formatCurrency } from 'src/app/services/constants';
import {
  actionsForTarget,
  normalizeCreditActions,
  resolveCreditBounds,
  VoyageCreditAction,
} from 'src/app/services/operators/voyageradar-credit-actions';

/**
 * Voyage Radar API credit purchase.
 *
 * Extracted from OperatorPopupComponent, which serves six operators from one
 * class. The result contract is deliberately identical so no caller changed:
 * `{ apiCredits }` on buy, the string 'close' on cancel — every call site does
 * `if (result != 'close')`.
 *
 * Numbers come from the operator document (`apiCreditActions`, `minApiCredits`,
 * `maxApiCredits`, `latency[].unitcost`) via the sensor row, with code-side
 * defaults behind them so a bad ee-admin edit cannot produce a screen nobody
 * can satisfy. The bounds are re-checked server-side at task creation — this
 * validation is a courtesy to the user, not the control.
 */
@Component({
  selector: 'app-voyage-credit-purchase',
  templateUrl: './voyage-credit-purchase.component.html',
  styleUrls: ['./voyage-credit-purchase.component.scss'],
})
export class VoyageCreditPurchaseComponent implements OnInit {
  creditFormGroup: FormGroup;

  /**
   * Held directly rather than reached through `creditFormGroup.get('credits')`,
   * which is typed `AbstractControl | null` and would need a non-null assertion
   * at every use. The control cannot be absent — this class creates it.
   */
  private creditsControl: FormControl;

  /**
   * Catalogue of spendable actions, doc-first and narrowed to the shape of the
   * target being tasked.
   *
   * The two refresh endpoints are still priced separately, but the catalogue no
   * longer makes them mutually exclusive per task. /v1/vessels/bbox still serves
   * an area and nothing else, so listing it to a point buyer would still price
   * an action this buyer can never reach — and keeping that one row off this
   * screen is the whole of what the narrowing still does. `nearby` is no longer
   * tagged as the point half of the pair: it prices the /v1/vessels/nearby
   * ENDPOINT rather than one gesture, and that endpoint is shaped for a second
   * caller — a search around a vessel the user selects, build steps 4-8 of the
   * nearby-vessel plan, NOT built yet. So it carries no target and is listed to
   * area and point buyers alike. An area buyer therefore draws both refresh rows
   * off the shipped catalogue, and that is intended rather than the double-count
   * the old exclusive pairing had to guard against — each row carries its own
   * "buys N" column and the screen says "…or any mix of these", so two
   * separately priced endpoints read as two things to spend on rather than as
   * the same purchase counted twice.
   *
   * Not what is on screen TODAY, though: this dialog filters the sensor row's
   * own `apiCreditActions` snapshot, and a live operator document still carries
   * the retired `refreshPoi` entry tagged 'poi', so one refresh row is all an
   * area buyer actually gets until the migration re-syncs the document — the
   * plan's §6.0, deliberately the last step of the release.
   */
  creditActions: VoyageCreditAction[] = [];

  /** USD price of one credit. 0 when the caller has no pricing context. */
  creditUnitPrice = 0;

  /**
   * Money is only rendered when a price is actually known. The Outcome/Budget
   * path opens this dialog with `selectedRow: {}` and no unitcost, so every
   * currency element drops out there and the credit content stands alone.
   */
  showCreditPricing = false;

  /** Always known — derived from the cheapest action when the doc is silent. */
  minCredits = 0;

  /** null when the operator doc sets no ceiling; the server then decides. */
  maxCredits: number | null = null;

  creditError = true;
  creditMessage = '';

  /**
   * Suggested amounts. Presentation only — they set the input and never
   * constrain it. Filtered to the live bounds so a chip can never offer an
   * amount the form would then reject.
   */
  creditChips: number[] = [];

  private activeUser: any;

  constructor(
    private dialogRef: MatDialogRef<VoyageCreditPurchaseComponent>,
    private authService: AuthService,
    @Inject(MAT_DIALOG_DATA) public data: any,
  ) {}

  ngOnInit(): void {
    // Subscribed FIRST, unlike the shared popup where activeUser is assigned on
    // the last line of a six-operator ngOnInit — which is why that file needed a
    // safeCostLabel() guard around every currency call. Here there is nothing
    // to guard.
    this.authService.user.subscribe((user) => (this.activeUser = user));

    const row = this.data?.selectedRow;

    // `!!` rather than a default of false, so a caller that omits the flag
    // entirely is read as an area task — the shape every pre-POI task had.
    // That branch is no longer a narrower list, mind: `refresh`/'aoi' is the
    // only targeted entry left, so an area task comes back with the whole
    // catalogue. It is the point branch that now does all the narrowing.
    this.creditActions = actionsForTarget(
      normalizeCreditActions(row?.apiCreditActions),
      !!this.data?.isPointTarget,
    );
    this.creditUnitPrice = Number(row?.unitcost ?? row?.latency?.[0]?.unitcost ?? 0);
    this.showCreditPricing = this.creditUnitPrice > 0;

    // Bounds off the FILTERED catalogue: the floor is the cheapest action this
    // task can actually spend on, so a wallet is never sold as usable on the
    // strength of an action this buyer cannot reach. That exclusion runs one
    // way rather than splitting symmetrically between the shapes — only
    // /v1/vessels/bbox is still restricted to one of them, so it is only a
    // point buyer who has an action held back, while an area buyer's bounds
    // come off the whole catalogue because the narrowing removes nothing from
    // it. Both shapes floor on the same 1-credit action today, so filtering
    // first changes no number on this screen; it is done anyway so the floor
    // stays honest the day ee-admin prices the area refresh below everything a
    // point task can call.
    const bounds = resolveCreditBounds(row, this.creditActions);
    this.minCredits = bounds.min;
    this.maxCredits = bounds.max;
    this.creditChips = this.buildChips(bounds.min, bounds.max);

    this.creditsControl = new FormControl('');
    this.creditFormGroup = new FormGroup({ credits: this.creditsControl });
    this.creditFormGroup.valueChanges.subscribe(() => this.validate());
    this.validate();
  }

  /**
   * Imperative validation, matching this dialog family's existing style — the
   * Buy button reads `creditError` directly, and an Angular Validators chain
   * alongside it would be a second source of truth for the same decision.
   */
  private validate(): void {
    const raw = this.creditsControl.value;
    const blank = raw === '' || raw === null || raw === undefined;
    const value = Number(raw);

    if (blank) {
      this.creditError = true;
      this.creditMessage = '';
      return;
    }

    if (!Number.isFinite(value) || value % 1 !== 0 || value < 1) {
      this.creditError = true;
      this.creditMessage = 'Enter a whole number of credits.';
      return;
    }

    if (value < this.minCredits) {
      this.creditError = true;
      this.creditMessage = this.showCreditPricing
        ? `The minimum purchase is ${this.formatCredits(this.minCredits)} credits ` +
          `(${this.costLabel(this.minCredits * this.creditUnitPrice)}).`
        : `The minimum purchase is ${this.formatCredits(this.minCredits)} credits.`;
      return;
    }

    if (this.maxCredits !== null && value > this.maxCredits) {
      this.creditError = true;
      this.creditMessage =
        `The maximum purchase is ${this.formatCredits(this.maxCredits)} credits. ` +
        `Task again for more.`;
      return;
    }

    this.creditError = false;
    this.creditMessage = '';
  }

  /**
   * Up to four suggestions spanning the range: both bounds plus round values
   * between them. The ends are kept deliberately — slicing a flat candidate
   * list drops the ceiling, which is the one amount a buyer most wants offered.
   * Nothing outside the bounds is ever suggested.
   *
   * With no ceiling from the document, the top suggestion is a presentational
   * stand-in only. Chips set the input; they never constrain it, so this
   * number bounds nothing and is not a limit in disguise.
   */
  private buildChips(min: number, max: number | null): number[] {
    const ceiling = max ?? min * 10;
    const between = [500, 1000, 2500].filter((n) => n > min && n < ceiling).slice(0, 2);
    const all = [min, ...between, ceiling];
    return [...new Set(all)].sort((a, b) => a - b);
  }

  setCredits(n: number): void {
    this.creditsControl.setValue(n);
  }

  get creditsEntered(): number {
    // Optional-chained: getters can be evaluated before ngOnInit assigns this.
    const v = Number(this.creditsControl?.value);
    return Number.isFinite(v) && v > 0 ? Math.trunc(v) : 0;
  }

  get creditTotalCost(): number {
    return this.creditUnitPrice * this.creditsEntered;
  }

  /** True once there is a valid amount worth summarising. */
  get hasSummary(): boolean {
    return !this.creditError && this.creditsEntered > 0;
  }

  /**
   * Thousands separators — a bare 5000 reads as a typo next to money.
   *
   * Takes the nullable form because `maxCredits` is one, and an `*ngIf` guard
   * in the template does not narrow a field for the call inside it. The `|| 0`
   * was already the runtime answer; the signature now says so.
   */
  formatCredits(n: number | null): string {
    return Number(n || 0).toLocaleString('en-US');
  }

  /**
   * The line under the field while nothing is wrong. It holds the same slot the
   * error takes, so the bounds are stated where the message that enforces them
   * will appear — and the row does not resize when one replaces the other.
   */
  get rangeHint(): string {
    const min = this.formatCredits(this.minCredits);
    return this.maxCredits === null
      ? `${min} credits or more`
      : `${min} – ${this.formatCredits(this.maxCredits)} credits`;
  }

  /** The headline figure — money when a price is known, credits when not. */
  get totalLabel(): string {
    if (!this.hasSummary) return '—';
    return this.showCreditPricing
      ? this.costLabel(this.creditTotalCost)
      : `${this.formatCredits(this.creditsEntered)} credits`;
  }

  /**
   * The line under it. Never empty, so the block holds its height.
   *
   * No longer repeats the unit price: it is stated permanently beside the
   * amount field a few rows up, and saying it twice inside one 250px column
   * read as two different numbers at a glance.
   */
  get totalSubLabel(): string {
    if (!this.hasSummary) return 'Choose or enter an amount.';
    return this.showCreditPricing
      ? `${this.formatCredits(this.creditsEntered)} credits`
      : 'Ready to buy.';
  }

  /** Plain and constant — the amount and cost are already stated above it. */
  get buyLabel(): string {
    return 'Buy credits';
  }

  /**
   * Currency or points, honouring the user's conversion setting. Points-based
   * customers must never be shown dollars.
   */
  costLabel(cost: number): string {
    if (this.activeUser?.pointsEligible && this.activeUser?.sensorConversionFactor) {
      const [part1, part2] = this.activeUser.sensorConversionFactor.split(':').map(parseFloat);
      const conversionFactor = part2 / part1;
      return `${Math.round(cost * conversionFactor)} Points`;
    }
    return formatCurrency(cost);
  }

  buyCredits(): void {
    if (this.creditError) return;
    this.dialogRef.close({ apiCredits: this.creditsEntered });
  }

  /** Cancel resolves 'close', which is what every caller tests for. */
  onNoClick(): void {
    this.dialogRef.close('close');
  }
}
